import React, { ReactNode, Suspense, useState, useEffect } from 'react';
import {
  useClientContext,
  PageLoader,
  useClientTokenManager,
  parseToken,
  CurrentClientProvider,
  EntityModel,
  SubEntityModel,
  ClientModel,
  SegmentModel,
  userStorage,
  useLogger,
} from '@cko/dashboard-shared';

const AuthError = React.lazy(() => import('components/AuthError/AuthError'));

export const ClientConfigurationManager = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasLoadedClient, setHasLoadedClient] = useState<boolean>(false);

  const [client, setClient] = useState<ClientModel | undefined>();
  const [entity, setEntity] = useState<EntityModel | undefined>();
  const [segment, setSegment] = useState<SegmentModel | undefined>();
  const [subEntity, setSubEntity] = useState<SubEntityModel | undefined>();

  const [availableSegments, setAvailableSegments] = useState<SegmentModel[] | undefined>();
  const [availableClientSegments, setAvailableClientSegements] = useState<
    SegmentModel[] | undefined
  >();

  const logger = useLogger();

  const setSegmentsFromClient = (segmentClient: ClientModel, entityId?: string) => {
    const allSegments: SegmentModel[] = segmentClient.entities.flatMap(
      (entityModel: EntityModel) => entityModel.entitySegments,
    );
    const entitySegments: SegmentModel[] = entityId
      ? allSegments.filter((sgmnt: SegmentModel) => sgmnt.entityId === entityId)
      : allSegments;
    setAvailableClientSegements(allSegments);
    setAvailableSegments(entitySegments);
  };

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const hasErrored = () => {
    setErrorMessage('No client or sub-entity is available for the current account');
    logger.warn('No client or sub-entity is available for the current account');
  };

  const {
    accessToken,
    loading: loadingToken,
    actions: { refreshToken },
  } = useClientTokenManager();

  const { getClient, getInitialClient } = useClientContext();

  const loadPreviousState = () => {
    const previousClient = userStorage.getClient();
    const previousEntity = userStorage.getEntity();
    const previousSegment = userStorage.getSegment();

    return (
      previousClient && { client: previousClient, entity: previousEntity, segment: previousSegment }
    );
  };

  const loadDefaults = async () => {
    if (accessToken) {
      const defaultToken = parseToken(accessToken);

      const isSubEntitySession = Boolean(defaultToken.cko_sub_entity_id);
      const isUserFromPreviousSession = defaultToken.cko_user_id
        ? userStorage.isUserFromPreviousSession(defaultToken.cko_user_id)
        : false;

      const previousState =
        isUserFromPreviousSession && !isSubEntitySession ? loadPreviousState() : undefined;

      const loadFreshClient = async () => {
        try {
          const defaultItem = await getInitialClient();
          if (defaultItem instanceof SubEntityModel) {
            setSubEntity(defaultItem);
          } else if (defaultItem instanceof ClientModel) {
            setClient(defaultItem);
            setSegmentsFromClient(defaultItem);
            if (defaultItem.id !== defaultToken.cko_client_id) {
              await refreshToken(defaultItem.id);
            }
          } else {
            hasErrored();
          }
          setIsLoading(false);
          setHasLoadedClient(true);
        } catch {
          hasErrored();
        }
      };

      const loadPreviousClient = async () => {
        if (previousState) {
          const previousClient = previousState.client;
          if (defaultToken.cko_client_id !== previousClient.id) {
            await refreshToken();
          } else {
            try {
              const newClient = await getClient(previousClient?.id);
              setClient(newClient);

              //try to see if we can set the entity from the new client instead of the old one to refesh segments list etc
              const foundEntity = newClient?.entities.find((newEnt) => {
                return newEnt.id === previousState.entity?.id;
              });
              if (foundEntity) {
                setEntity(foundEntity);
              } else {
                setEntity(previousState.entity);
              }

              if (newClient) {
                setSegmentsFromClient(newClient, previousState.entity?.id);
              }

              setSegment(previousState.segment);
              setIsLoading(false);
              setHasLoadedClient(true);
            } catch {
              logger.warn('Cant load previous client');
              userStorage.clearAll();
              await loadFreshClient();
            }
          }
        }
      };

      if (isSubEntitySession || !isUserFromPreviousSession) {
        userStorage.clearAll();
      }

      if (previousState === undefined) {
        await loadFreshClient();
      } else {
        await loadPreviousClient();
      }
    }
  };

  useEffect(() => {
    if (!hasLoadedClient) {
      loadDefaults();
    }
  }, [loadingToken]);

  return (
    <Suspense fallback={<PageLoader />}>
      {errorMessage && <AuthError errorCode={errorMessage} />}
      {isLoading && <PageLoader />}
      {!isLoading && !errorMessage && (
        <CurrentClientProvider
          defaultAvailableEntities={client?.entities}
          defaultAvailableSegments={availableSegments}
          defaultClientSegments={availableClientSegments}
          defaultClient={client}
          defaultEntity={entity}
          defaultSegment={segment}
          defaultSubEntity={subEntity}
        >
          {children}
        </CurrentClientProvider>
      )}
    </Suspense>
  );
};
